import * as XLSX from "xlsx";
import { STANDARD, BLUE_DREAM, JC_ALQURE, TEMPLATE_HEADERS } from "./expenseTemplates";

// Columns of each branch template's spreadsheet. Matched by header text
// (case/spacing/punctuation-insensitive), never by position, so a reordered or
// extra-column sheet still parses correctly.
//
// BLUE_DREAM reads its columns differently from STANDARD on purpose: there
// AMOUNT is the price before VAT and NET AMOUNT is the VAT-inclusive figure
// (the opposite of STANDARD's NET AMOUNT = before VAT), so NET AMOUNT is only
// used to warn when it disagrees with the recalculated total.
const HEADER_ALIASES = {
  [STANDARD]: {
    job_schedule_date: ["jobscheduledate", "jobdate", "scheduledate"],
    trucking_date: ["truckingdate"],
    vin_number: ["vinnumber", "vinno", "vin"],
    model: ["model"],
    classification: ["classification"],
    washing: ["washing"],
    polishing: ["polishing"],
    pdi: ["pdi"],
    valeting: ["valeting"],
    net_amount: ["netamount", "net"],
    vat: ["vat", "vatpercent", "vatpct"],
    total: ["totalamount", "total"],
  },
  [BLUE_DREAM]: {
    sr_no: ["srno", "serialno", "slno", "sno"],
    job_schedule_date: ["jobscheduledate", "jobdate", "scheduledate", "date"],
    vin_number: ["vinnumber", "vinno", "vin"],
    model: ["model"],
    classification: ["classification"],
    net_amount: ["amount"],
    vat: ["vat", "vatpercent", "vatpct"],
    total: ["netamount"],
  },
  [JC_ALQURE]: {
    sr_no: ["sno", "srno", "serialno", "slno"],
    job_schedule_date: ["date", "jobscheduledate", "jobdate", "scheduledate"],
    description: ["description"],
    vin_number: ["vehiclenumber", "vehicleno", "vehicle", "platenumber", "plateno", "plate"],
    model: ["model"],
    unit_price: ["price", "unitprice", "rate"],
    qty: ["qty", "quantity"],
    vat: ["vat", "vatpercent", "vatpct"],
    total: ["total", "totalamount"],
  },
};

export const MAX_SHEET_ROWS = 2000;
const HEADER_SEARCH_ROWS = 10;
const DEFAULT_VAT_PCT = 5;

const normHeader = (v) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const pad = (n) => String(n).padStart(2, "0");
// Trim and collapse inner runs of spaces (incl. non-breaking) - "  Wash" -> "Wash".
const tidy = (v) => String(v ?? "").replace(/[\s ]+/g, " ").trim();

const ymd = (y, m, d) => {
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
};

const MONTH_WORD = /^(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)$/i;
const MONTH_ORDER = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const monthNumber = (word) => {
  const m = MONTH_WORD.exec(word);
  return m ? MONTH_ORDER.indexOf(m[1].slice(0, 3).toLowerCase()) + 1 : null;
};

// Real Excel date cells arrive as Date objects (cellDates:true) - unambiguous,
// so locale never matters. +12h then UTC parts absorbs SheetJS's small
// timezone skew. Text cells are ALWAYS day-first: 01.09.2026, 01/09/2026,
// 01-09-2026, 1/9/26 are all 1 September; 01-Sep-2026, 1 Sep 2026 and
// 1-Sept-26 name the month. There is no month-first fallback, so 09/13/2026
// is rejected (month 13) rather than guessed. A trailing time (01.09.2026
// 00:00), a trailing dot and stray/non-breaking spaces are tolerated; 2-digit
// years are 20yy.
export function toYmd(value) {
  if (isBlank(value)) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const shifted = new Date(value.getTime() + 12 * 3600 * 1000);
    return ymd(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
  }
  if (typeof value === "number") {
    const p = XLSX.SSF.parse_date_code(value);
    return p ? ymd(p.y, p.m, p.d) : null;
  }
  const s = tidy(value)
    .replace(/[\sT]+\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?\s*(?:am|pm)?\s*z?$/i, "")
    .replace(/[.,]+$/, "");
  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) return ymd(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})(?:st|nd|rd|th)?[-\s/.]+([A-Za-z]{3,9})\.?[-\s/.,]+(\d{2}|\d{4})$/.exec(s);
  if (m) {
    const month = monthNumber(m[2]);
    if (!month) return null;
    return ymd(m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]), month, Number(m[1]));
  }
  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    return ymd(y, Number(m[2]), Number(m[1]));
  }
  return null;
}

export const toFlag = (v) => v === true || v === 1 || /^(y|yes|true|1)$/i.test(String(v ?? "").trim());

const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (isBlank(v)) return null;
  const cleaned = String(v).replace(/[^0-9.-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

// "5%" -> 5, 0.05 (a percent-formatted cell) -> 5, 5 -> 5, blank -> 5 (UAE standard).
export function toVatPct(v) {
  if (isBlank(v)) return DEFAULT_VAT_PCT;
  if (typeof v === "string" && v.includes("%")) return toNumber(v);
  const n = toNumber(v);
  if (n === null) return null;
  return n > 0 && n <= 1 ? Math.round(n * 10000) / 100 : n;
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const TEMPLATE_NAME = {
  [STANDARD]: "standard",
  [BLUE_DREAM]: "Blue Dream Auto Maint",
  [JC_ALQURE]: "job card (Al Quze / JBL)",
};

// Columns that must exist for each template, beyond the VIN + date pair used to
// locate the header row. [key, label shown in the error].
const REQUIRED_COLUMNS = {
  [STANDARD]: [["net_amount", "NET AMOUNT"]],
  [BLUE_DREAM]: [["net_amount", "AMOUNT"]],
  [JC_ALQURE]: [["description", "Description"], ["unit_price", "Price"]],
};

const MISMATCHED_HEADER_ERROR = (template) => {
  if (template === STANDARD) return "Could not find the header row. Expected columns like JOB SCHEDULE DATE and VIN NUMBER.";
  return `This file doesn't match this branch's ${TEMPLATE_NAME[template]} template. Expected columns: ${TEMPLATE_HEADERS[template].join(", ")}.`;
};

// matrix: array of row arrays (as from sheet_to_json header:1, raw:true).
// template: the branch's upload template. Returns { rows, error }. Each row
// carries `errors` (blocking issues) and, when the sheet's own total disagrees
// with the recalculated one, `totalMismatch`.
export function parseSheetMatrix(matrix, template = STANDARD) {
  const aliasMap = HEADER_ALIASES[template] || HEADER_ALIASES[STANDARD];
  const vehicleLabel = template === JC_ALQURE ? "Vehicle number" : "VIN number";
  let headerIdx = -1;
  let colMap = {};
  for (let i = 0; i < Math.min(matrix.length, HEADER_SEARCH_ROWS); i += 1) {
    const cells = (matrix[i] || []).map(normHeader);
    const found = {};
    Object.entries(aliasMap).forEach(([key, aliases]) => {
      const idx = cells.findIndex((c) => aliases.includes(c));
      if (idx !== -1) found[key] = idx;
    });
    if (found.vin_number !== undefined && found.job_schedule_date !== undefined) {
      // A Blue Dream sheet (AMOUNT + NET AMOUNT) read as STANDARD would take the
      // VAT-inclusive NET AMOUNT as the pre-VAT price and add VAT again.
      if (template === STANDARD && cells.includes("amount") && cells.includes("netamount")) {
        return {
          rows: [],
          error: "This looks like the Blue Dream template (AMOUNT and NET AMOUNT columns). Set this branch's Upload template to Blue Dream in Branch Setup.",
        };
      }
      headerIdx = i;
      colMap = found;
      break;
    }
  }
  if (headerIdx === -1) return { rows: [], error: MISMATCHED_HEADER_ERROR(template) };
  const missing = (REQUIRED_COLUMNS[template] || []).find(([key]) => colMap[key] === undefined);
  if (missing) return { rows: [], error: `The ${missing[1]} column is missing.` };

  const cell = (row, key) => (colMap[key] === undefined ? undefined : row[colMap[key]]);
  const rows = [];
  for (let i = headerIdx + 1; i < matrix.length; i += 1) {
    const r = matrix[i] || [];
    if (r.every(isBlank)) continue;
    // Skip stray summary lines (no VIN and no date at all).
    if (isBlank(cell(r, "vin_number")) && isBlank(cell(r, "job_schedule_date"))) continue;

    const errors = [];
    const vin = String(cell(r, "vin_number") ?? "").trim().toUpperCase();
    if (!vin) errors.push(`${vehicleLabel} is required`);

    const jobDate = toYmd(cell(r, "job_schedule_date"));
    if (!jobDate) errors.push("Job schedule date is missing or not a valid date");

    let truckingDate = null;
    if (!isBlank(cell(r, "trucking_date"))) {
      truckingDate = toYmd(cell(r, "trucking_date"));
      if (!truckingDate) errors.push("Trucking date is not a valid date");
    }

    let description = "";
    let unitPrice = null;
    let qty = 1;
    let net;
    if (template === JC_ALQURE) {
      description = tidy(cell(r, "description"));
      if (!description) errors.push("Description is required");
      unitPrice = toNumber(cell(r, "unit_price"));
      if (unitPrice === null || unitPrice < 0) errors.push("Price must be a number");
      if (!isBlank(cell(r, "qty"))) {
        qty = toNumber(cell(r, "qty"));
        if (qty === null || qty <= 0) errors.push("Qty must be greater than 0");
      }
      net = unitPrice === null || qty === null ? null : round2(unitPrice * qty);
    } else {
      net = toNumber(cell(r, "net_amount"));
      if (net === null || net < 0) errors.push(template === BLUE_DREAM ? "Amount must be a number" : "Net amount must be a number");
    }

    const vatPct = toVatPct(cell(r, "vat"));
    if (vatPct === null || vatPct < 0 || vatPct > 100) errors.push("VAT % is not valid");

    const netAmount = net === null ? 0 : round2(net);
    const vatAmount = vatPct === null ? 0 : round2((netAmount * vatPct) / 100);
    const totalAmount = round2(netAmount + vatAmount);
    const sheetTotal = toNumber(cell(r, "total"));

    rows.push({
      rowNumber: i + 1, // Excel row, used to point at a row in errors
      // The sheet's own serial number (SR NO / S No), shown in the preview; null when the template has none or the cell is blank.
      srNo: isBlank(cell(r, "sr_no")) ? null : String(cell(r, "sr_no")).trim(),
      job_schedule_date: jobDate,
      trucking_date: truckingDate,
      vin_number: vin,
      model: template === STANDARD ? String(cell(r, "model") ?? "").trim() : tidy(cell(r, "model")),
      classification: template === STANDARD ? String(cell(r, "classification") ?? "").trim() : tidy(cell(r, "classification")),
      description,
      unit_price: unitPrice === null ? null : round2(unitPrice),
      qty: qty === null ? 1 : qty,
      washing: toFlag(cell(r, "washing")),
      polishing: toFlag(cell(r, "polishing")),
      pdi: toFlag(cell(r, "pdi")),
      valeting: toFlag(cell(r, "valeting")),
      net_amount: netAmount,
      vat_pct: vatPct === null ? DEFAULT_VAT_PCT : vatPct,
      vat_amount: vatAmount,
      total_amount: totalAmount,
      totalMismatch: sheetTotal !== null && Math.abs(sheetTotal - totalAmount) > 0.01 ? sheetTotal : null,
      errors,
    });
  }

  if (!rows.length) return { rows: [], error: "No expense rows found below the header." };
  if (rows.length > MAX_SHEET_ROWS) {
    return { rows: [], error: `This file has ${rows.length} rows. Please upload at most ${MAX_SHEET_ROWS} rows at a time.` };
  }
  return { rows, error: null };
}

export async function parseExpenseFile(file, template = STANDARD) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return { rows: [], error: "The file has no sheets." };
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
  return parseSheetMatrix(matrix, template);
}

// Only the fields the API accepts - never the sheet's own VAT amount / total.
// The JC_ALQURE fields (description, unit_price, qty) are sent only for that
// template; net_amount is then derived from them on the server.
export const toApiRow = (row, template = STANDARD) => ({
  vin_number: row.vin_number,
  job_schedule_date: row.job_schedule_date,
  trucking_date: row.trucking_date,
  model: row.model,
  classification: row.classification,
  washing: row.washing,
  polishing: row.polishing,
  pdi: row.pdi,
  valeting: row.valeting,
  net_amount: row.net_amount,
  vat_pct: row.vat_pct,
  ...(template === JC_ALQURE ? { description: row.description, unit_price: row.unit_price, qty: row.qty } : {}),
});

export const TEMPLATE_DATE_FORMAT = "dd-mmm-yyyy";

// One sample row per template (dates as real Date objects).
const SAMPLE_ROWS = {
  [STANDARD]: [new Date(2026, 8, 1), new Date(2026, 8, 2), "HJNNAERD3SB625602", "EL8", "PDI", "Yes", "Yes", "Yes", "No", 110, "5%", 115.5],
  [BLUE_DREAM]: [1, new Date(2026, 8, 1), "HJNNAERD3SB625602", "EL8", "PDI", 110, "5%", 115.5],
  [JC_ALQURE]: [1, new Date(2026, 8, 1), "VALETING", "60829", "MAZDA 6", 85, 1, "5%", 89.25],
};
const DATE_COLUMNS = { [STANDARD]: [0, 1], [BLUE_DREAM]: [1], [JC_ALQURE]: [1] };
const SHEET_NAME = { [STANDARD]: "Expenses", [BLUE_DREAM]: "Expenses", [JC_ALQURE]: "Sheet1" };

// The downloadable template: dates are REAL date cells formatted dd-mmm-yyyy, so
// the author sees 01-Sep-2026 in Excel whatever the computer's region, instead
// of a numeric date a month-first Excel would re-read as 9 January.
export function buildTemplateWorkbook(template = STANDARD) {
  const key = SAMPLE_ROWS[template] ? template : STANDARD;
  const headers = TEMPLATE_HEADERS[key];
  const ws = XLSX.utils.aoa_to_sheet([headers, SAMPLE_ROWS[key]], { cellDates: true });
  DATE_COLUMNS[key].forEach((col) => {
    ws[XLSX.utils.encode_cell({ r: 1, c: col })].z = TEMPLATE_DATE_FORMAT;
  });
  const dateCols = DATE_COLUMNS[key];
  ws["!cols"] = headers.map((h, i) => ({ wch: dateCols.includes(i) ? 20 : /vin|vehicle/i.test(h) ? 22 : Math.max(h.length + 2, 12) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME[key]);
  return wb;
}
